/**
 * Mobile/PWA overlay back stack.
 *
 * Each open overlay pushes a history entry. Android/browser Back pops that
 * entry and closes only the top overlay — the page route stays put.
 */

export type OverlayBackCloser = () => void;

type OverlayBackEntry = {
  id: string;
  close: OverlayBackCloser;
};

const HISTORY_FLAG = '__gtOverlay';

let stack: OverlayBackEntry[] = [];
/** True while we call history.back() after a programmatic (X/mask) close. */
let syncingHistory = false;
/** Ids closed via popstate — release must not history.back() again. */
const closedByPopstate = new Set<string>();
let listenerBound = false;

const EPHEMERAL_ID = 'ant-ephemeral-overlay';

function ensureListener(): void {
  if (typeof window === 'undefined' || listenerBound) {
    return;
  }
  listenerBound = true;
  window.addEventListener('popstate', handlePopState);
}

function handlePopState(): void {
  if (syncingHistory) {
    syncingHistory = false;
    return;
  }
  const top = stack.pop();
  if (!top) {
    return;
  }
  closedByPopstate.add(top.id);
  try {
    top.close();
  } catch {
    // Overlay may already be unmounted.
  }
}

/**
 * Register an overlay so the next Back press closes it.
 * Safe to call again for the same id (updates the closer).
 */
export function pushOverlayBack(id: string, close: OverlayBackCloser): void {
  if (typeof window === 'undefined') {
    return;
  }
  ensureListener();

  const existing = stack.find((entry) => entry.id === id);
  if (existing) {
    existing.close = close;
    return;
  }

  // ⋯ menus register as ephemeral; opening a modal on the next frame can leave a
  // stale entry under the sheet and break Back / history sync on close.
  if (id !== EPHEMERAL_ID) {
    const ephemeralIdx = stack.findIndex((entry) => entry.id === EPHEMERAL_ID);
    if (ephemeralIdx !== -1) {
      stack.splice(ephemeralIdx, 1);
    }
  }

  stack.push({ id, close });
  const prev =
    typeof history.state === 'object' && history.state !== null
      ? (history.state as Record<string, unknown>)
      : {};
  history.pushState({ ...prev, [HISTORY_FLAG]: id }, '');
}

/**
 * Drop a history entry when the overlay closed via UI (not via Back).
 */
export function releaseOverlayBack(id: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (closedByPopstate.has(id)) {
    closedByPopstate.delete(id);
    return;
  }

  const idx = stack.findIndex((entry) => entry.id === id);
  if (idx === -1) {
    return;
  }

  const isTop = idx === stack.length - 1;
  stack.splice(idx, 1);

  if (!isTop) {
    // Stale middle entry — leave history alone; next Back may be a no-op closer.
    return;
  }

  const state = history.state as Record<string, unknown> | null;
  if (state && state[HISTORY_FLAG] === id) {
    syncingHistory = true;
    history.back();
  }
}

/** Test / hot-reload helper. */
export function resetOverlayBackStackForTests(): void {
  stack = [];
  syncingHistory = false;
  closedByPopstate.clear();
}

export function getOverlayBackStackDepthForTests(): number {
  return stack.length;
}

const EPHEMERAL_SELECTORS = [
  '.ant-dropdown:not(.ant-dropdown-hidden)',
  '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
  '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
  '.ant-popover:not(.ant-popover-hidden)',
  '.ant-mentions-dropdown:not(.ant-mentions-dropdown-hidden)',
  '.ant-cascader-menus',
].join(', ');

function isEphemeralOverlayVisible(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  return Boolean(document.querySelector(EPHEMERAL_SELECTORS));
}

function dismissEphemeralOverlay(): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.dispatchEvent(
    new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
      bubbles: true,
      cancelable: true,
    })
  );
}

/**
 * Watch Ant Design dropdowns / selects / pickers / popovers and treat them
 * as one-step Back targets (e.g. Transactions ⋯ menus).
 */
export function startAntEphemeralOverlayBackWatch(): () => void {
  if (typeof document === 'undefined') {
    return () => undefined;
  }
  ensureListener();

  let registered = false;

  const sync = () => {
    const visible = isEphemeralOverlayVisible();
    if (visible && !registered) {
      registered = true;
      pushOverlayBack(EPHEMERAL_ID, () => {
        registered = false;
        dismissEphemeralOverlay();
      });
    } else if (!visible && registered) {
      registered = false;
      releaseOverlayBack(EPHEMERAL_ID);
    }
  };

  const observer = new MutationObserver(() => {
    // Defer so Ant can finish applying open/hidden classes.
    window.requestAnimationFrame(sync);
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
  });
  sync();

  return () => {
    observer.disconnect();
    if (registered) {
      registered = false;
      releaseOverlayBack(EPHEMERAL_ID);
    }
  };
}
